import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type RefObject,
} from 'react';
import { sentenceEndOffsets } from './sentence-offsets';

/**
 * Where the copy is cut so the column ends at the foot of the image beside it.
 *
 * `null` while the copy is being measured or is not cut; otherwise a character
 * offset into the plain text, always the end of a sentence — a description
 * cut mid-line reads as broken rather than shortened. The offset is the
 * whole text when the description fits and only what follows it does not.
 */
export type CropToImage = { at: number } | null;

/**
 * Measures a copy column against the image beside it and decides the crop.
 * Shared by the project's About section and the product's details, so the
 * two columns stop at their pictures the same way.
 *
 * Two passes. While `crop` is null everything is drawn inside a box capped at
 * the image's height, and a layout effect — before paint, so the cut-off
 * content is never seen — checks whether it overflows. When it does, the
 * sentence ends are laid against a hidden twin of the plain text at the
 * column's width, and the last one that leaves room for Read more on its
 * line becomes the cut. Measured rather than styled because the image is a
 * square on a percentage width, so its height is whatever the row makes it.
 * The image is watched, and a resize starts the passes again. The caller
 * turns it off below md, where the columns stack and there is nothing to run
 * past.
 */
export const useCropToImage = (
    imageRef: RefObject<HTMLElement | null>,
    enabled: boolean,
    plainText: string,
) => {
    const copyRef = useRef<HTMLDivElement>(null);
    const proseRef = useRef<HTMLElement>(null);
    const twinRef = useRef<HTMLParagraphElement>(null);
    const [limit, setLimit] = useState<number | null>(null);
    const [crop, setCrop] = useState<CropToImage>(null);
    // Bumped by the image resizing, which sends the passes round again.
    const [pass, setPass] = useState(0);

    useEffect(() => {
        const image = imageRef.current;
        if (!enabled || !image) return;
        const observer = new ResizeObserver(() => {
            setCrop(null);
            setPass((n) => n + 1);
        });
        observer.observe(image);
        return () => observer.disconnect();
    }, [enabled, imageRef]);

    useLayoutEffect(() => {
        if (!enabled) {
            setLimit(null);
            setCrop(null);
            return;
        }
        if (crop !== null) return;
        const image = imageRef.current;
        const copy = copyRef.current;
        if (!image || !copy) return;
        const height = image.getBoundingClientRect().height;
        setLimit(height);
        if (copy.scrollHeight <= height + 1) return;

        const prose = proseRef.current;
        const twin = twinRef.current;
        const node = twin?.firstChild;
        if (!prose || !twin || !node) {
            setCrop({ at: 0 });
            return;
        }
        // What the prose may take: the cap, less what stands above it.
        const available =
            height -
            (prose.getBoundingClientRect().top -
                copy.getBoundingClientRect().top);
        const lineHeight = parseFloat(getComputedStyle(twin).lineHeight) || 0;
        const twinTop = twin.getBoundingClientRect().top;
        const range = document.createRange();
        const bottomAt = (offset: number) => {
            range.setStart(node, 0);
            range.setEnd(node, Math.min(offset, plainText.length));
            return range.getBoundingClientRect().bottom - twinTop;
        };
        if (bottomAt(plainText.length) + lineHeight <= available) {
            // The description fits whole; it is what follows it that does
            // not, so the cut is at the end of the description.
            setCrop({ at: plainText.length });
            return;
        }
        // The last sentence end that leaves a line for Read more.
        const offsets = sentenceEndOffsets(plainText);
        let at = offsets[0] ?? plainText.length;
        for (const offset of offsets) {
            if (bottomAt(offset) + lineHeight > available) break;
            at = offset;
        }
        setCrop({ at });
    }, [enabled, crop, pass, plainText, imageRef]);

    return { copyRef, proseRef, twinRef, limit, crop };
};
